import { Controller } from "@hotwired/stimulus"

/**
 * Controller para el toggle de "En Ruta" en tickets.
 * Permite al mecánico incluir/excluir tickets de la ruta del día.
 * Maneja automáticamente cambios de estado cuando es necesario.
 */
export default class extends Controller {
  static targets = ["button", "toggle"]
  static values = {
    url: String,
    ticketId: Number
  }

  async toggle(event) {
    event.stopPropagation() // Prevent card zoom from triggering

    const button = this.buttonTarget
    const toggleSpan = this.toggleTarget
    const isCurrentlyOn = button.getAttribute('aria-checked') === 'true'
    const newValue = !isCurrentlyOn

    // Optimistic UI update
    this.updateUI(newValue)

    try {
      const response = await fetch(this.urlValue, {
        method: 'PATCH',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'text/vnd.turbo-stream.html',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        },
        body: JSON.stringify({
          ticket: { en_ruta: newValue }
        })
      })

      if (response.ok) {
        // Turbo Streams procesará automáticamente la respuesta y actualizará el DOM
        const html = await response.text()
        console.log(`📡 Response HTML length: ${html.length}`)
        console.log(`📡 Full Turbo Stream response:`, html)
        
        // Check if turbo-stream remove is in the response
        if (html.includes('action="remove"')) {
          console.log('✅ REMOVE action found in response')
        } else {
          console.log('❌ NO REMOVE action in response - ticket stays in place')
        }
        
        Turbo.renderStreamMessage(html)
        console.log(`🚴 Ticket ${this.ticketIdValue} en_ruta: ${newValue} - Turbo Stream processed`)
        
        // Dispatch event to refresh route
        window.dispatchEvent(new CustomEvent('en-ruta-changed', {
          detail: {
            ticketId: this.ticketIdValue,
            enRuta: newValue
          }
        }))
        
        // Refresh route position badges
        this.refreshRoutePositions()
        
        // Refresh route position badges
        this.refreshRoutePositions()
      } else {
        // Revert on error
        console.error('Error updating en_ruta', response.status)
        this.updateUI(!newValue)
        
        // Try to get error message from response
        try {
          const errorData = await response.json()
          console.error('Server error:', errorData.error)
          this.showError(errorData.error || 'No se pudo actualizar el estado')
        } catch {
          this.showError('No se pudo actualizar el estado')
        }
      }
    } catch (error) {
      console.error('Error updating en_ruta:', error)
      this.updateUI(!newValue)
      this.showError('Error de conexión')
    }
  }

  async refreshRoutePositions() {
    try {
      const today = new Date().toISOString().split('T')[0]
      const response = await fetch(`/apps/home-repairs/tickets/route_positions?date=${today}`)
      
      if (response.ok) {
        const positions = await response.json()
        this.updateRoutePositionBadges(positions)
      }
    } catch (error) {
      console.error('Error refreshing route positions:', error)
    }
  }

  updateRoutePositionBadges(positions) {
    positions.forEach(position => {
      const ticketElement = document.querySelector(`[data-ticket-id="${position.ticket_id}"]`)
      if (ticketElement) {
        const badge = ticketElement.querySelector('[data-route-position] span')
        if (badge) {
          badge.textContent = position.position
        }
        
        // Update badge visibility
        const badgeContainer = ticketElement.querySelector('[data-route-position]')
        if (badgeContainer) {
          badgeContainer.style.display = position.position > 0 ? 'flex' : 'none'
        }
      }
    })
  }

  updateUI(isOn) {
    const button = this.buttonTarget
    const toggleSpan = this.toggleTarget
    const container = this.element
    const label = container.querySelector('span')

    button.setAttribute('aria-checked', isOn)

    // Quitar todas las clases de color posibles
    button.classList.remove('bg-gray-200', 'bg-gray-600', 'bg-green-200')

    if (isOn) {
      button.classList.add('bg-gray-600')
      toggleSpan.classList.remove('translate-x-0')
      toggleSpan.classList.add('translate-x-6', 'sm:translate-x-3')
      if (label) label.classList.remove('line-through')
    } else {
      button.classList.add('bg-gray-200')
      toggleSpan.classList.remove('translate-x-6', 'sm:translate-x-3')
      toggleSpan.classList.add('translate-x-0')
      if (label) label.classList.add('line-through')
    }
  }

  showError(message = 'No se pudo actualizar') {
    const button = this.buttonTarget
    button.classList.add('ring-2', 'ring-red-500')
    setTimeout(() => {
      button.classList.remove('ring-2', 'ring-red-500')
    }, 1000)

    // Mostrar notificación de error
    const notification = document.createElement('div')
    notification.className = 'fixed bottom-4 right-4 bg-red-100 border border-red-400 text-red-800 px-4 py-3 rounded-lg shadow-lg z-50 max-w-sm'
    notification.innerHTML = `
      <div class="flex items-start gap-2">
        <svg class="w-5 h-5 shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd"/>
          </svg>
        <span>${message}</span>
      </div>
    `
    document.body.appendChild(notification)
    
    // Remover después de 5 segundos
    setTimeout(() => {
      notification.classList.add('opacity-0', 'transition-opacity', 'duration-300')
      setTimeout(() => notification.remove(), 300)
    }, 5000)
  }

  showStateChangeNotification(estadoAnterior, estadoNuevo) {
    // Mostrar notificación de cambio de estado
    const notification = document.createElement('div')
    notification.className = 'fixed bottom-4 right-4 bg-amber-100 border border-amber-400 text-amber-800 px-4 py-3 rounded-lg shadow-lg z-50 animate-fade-in'
    notification.innerHTML = `
      <div class="flex items-center gap-2">
        <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd"/>
          </svg>
        <span><strong>Estado cambiado:</strong> ${estadoAnterior} → ${estadoNuevo}</span>
      </div>
    `
    document.body.appendChild(notification)
    
    // Remover después de 4 segundos
    setTimeout(() => {
      notification.classList.add('animate-fade-out')
      setTimeout(() => notification.remove(), 300)
    }, 4000)
  }

  showDateChangeNotification(fechaAnterior, fechaNueva) {
    // Mostrar notificación de ticket reprogramado
    const notification = document.createElement('div')
    notification.className = 'fixed bottom-4 right-4 bg-green-100 border border-green-400 text-green-800 px-4 py-3 rounded-lg shadow-lg z-50'
    notification.innerHTML = `
      <div class="flex items-center gap-2">
        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7"/>
          </svg>
        <span><strong>Ticket añadido a la ruta de hoy</strong><br>
        <span class="text-sm">Reprogramado desde ${fechaAnterior || 'sin fecha'}. Recargando...</span></span>
      </div>
    `
    document.body.appendChild(notification)
  }

  updateTicketState(nuevoEstado) {
    // Buscar el badge de estado en la tarjeta del ticket y actualizarlo
    const card = this.element.closest('[id^="ticket_"]')
    if (!card) return

    const estadoBadge = card.querySelector('[data-estado-badge]')
    const estadoText = card.querySelector('[data-estado-text]')
    
    if (estadoText) {
      estadoText.textContent = `⏱️ ${nuevoEstado}`
    }
    
    if (estadoBadge) {
      // Quitar clases de colores anteriores
      estadoBadge.classList.remove(
        'bg-green-50', 'text-green-700',
        'bg-red-50', 'text-red-700',
        'bg-blue-50', 'text-blue-700',
        'bg-yellow-50', 'text-yellow-700',
        'bg-gray-50', 'text-gray-700'
      )
      
      // Añadir colores según el nuevo estado
      const colorMap = {
        'Cerrado': ['bg-green-50', 'text-green-700'],
        'Nuevo': ['bg-red-50', 'text-red-700'],
        'Esperando al contacto': ['bg-blue-50', 'text-blue-700'],
        'Esperando acción por nuestra parte': ['bg-yellow-50', 'text-yellow-700']
      }
      
      const colors = colorMap[nuevoEstado] || ['bg-gray-50', 'text-gray-700']
      estadoBadge.classList.add(...colors)
    }
  }
}
